import { ParsedUrlQuery } from 'querystring'
import { ToggleButtonGroup, ToggleButton } from '@mui/material'
import { Box } from '@mui/system'
import { onChangeFunc } from 'components/core/search-filter/filter'
import { taskStateToString } from 'components/pages/task/state'
import { useRouter } from 'next/router'
import { TaskState } from 'pbts/c1/api/task/v1/task'
import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export const taskStateQueryParam = 'state'

type taskStateToggleProps = {
    selected: TaskState[] | undefined
    onChange: onChangeFunc<TaskState>
}

export const TaskStateToggle = ({ selected, onChange }: taskStateToggleProps): React.ReactElement => {
    const intl = useIntl()
    const router = useRouter()

    const [loaded, setLoaded] = useState<boolean>(false)

    const fromQuery = useCallback((query: ParsedUrlQuery): TaskState[] => {
        let states = query[taskStateQueryParam]
        if (!states) {
            return []
        }

        if (!Array.isArray(states)) {
            states = [states]
        }

        const parsedStates: TaskState[] = []
        states.forEach((stateStr) => {
            const state = TaskState[stateStr]
            if (!state) {
                return
            }
            parsedStates.push(state)
        })

        return parsedStates
    }, [])

    useEffect(() => {
        if (loaded || !fromQuery) {
            return
        }
        setLoaded(true)

        const selectedFromQuery = fromQuery(router.query)

        onChange(selectedFromQuery, true)
    }, [router.query, loaded, fromQuery, onChange])

    const toQuery = useCallback((query: ParsedUrlQuery, states: TaskState[]): void => {
        const stateStrs: string[] = []
        states.forEach((state) => {
            const stateStr = taskStateToString(state)
            if (!stateStr) {
                return
            }
            stateStrs.push(stateStr)
        })

        if (stateStrs.length !== 0) {
            query[taskStateQueryParam] = stateStrs
        } else {
            delete query[taskStateQueryParam]
        }
    }, [])

    const handleStateFilterChange = (_, newState): void => {
        if (newState !== null) {
            onChange([newState], false)
            toQuery && toQuery(router.query, [newState])
        }
    }

    useEffect(() => {
        if (selected?.length === 0) {
            onChange([TaskState.OPEN], false)
        }
    }, [selected, onChange])

    return (
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <ToggleButtonGroup
                value={selected?.[0] || TaskState.OPEN}
                exclusive
                onChange={handleStateFilterChange}
                aria-label={intl.formatMessage({ id: 'my_requests_filter_label', defaultMessage: 'Task state' })}
                size="small"
            >
                <ToggleButton value={TaskState.OPEN} aria-label="Open requests">
                    <FormattedMessage
                        id="my_requests_filter_open"
                        defaultMessage="Open"
                    />
                </ToggleButton>
                <ToggleButton value={TaskState.CLOSED} aria-label="Completed requests">
                    <FormattedMessage
                        id="my_requests_filter_completed"
                        defaultMessage="Completed"
                    />
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    )
}
